export interface DraftVersion {
  text: string;
  revision: number;
}
export interface DraftState extends DraftVersion {
  status: 'saved' | 'dirty' | 'saving' | 'error' | 'conflict';
  error: string | null;
}
export interface PendingExplanation {
  baseline: DraftVersion;
  text: string;
  /** A PATCH at this revision may still commit after a lost response or reload. */
  pendingRevision?: number;
}

/** Keeps local typing independent of polling and revision-checked network responses. */
export class ExplanationDraft {
  private baseline: DraftVersion;
  private incoming: DraftVersion | null = null;
  private deferred: DraftVersion | null = null;
  private snapshot: DraftState;
  private listeners = new Set<() => void>();
  private saving: Promise<void> | null = null;
  private pendingRevision: number | null = null;

  constructor(
    initial: DraftVersion,
    private readonly persist: (draft: DraftVersion) => Promise<DraftVersion>,
    pending: PendingExplanation | null = null,
    private readonly retain: (pending: PendingExplanation | null) => void = () => undefined,
  ) {
    this.baseline = initial;
    this.snapshot = { ...initial, status: 'saved', error: null };
    const unconfirmed =
      pending?.pendingRevision !== undefined && initial.revision <= pending.pendingRevision;
    if (pending && (pending.text !== initial.text || unconfirmed)) {
      this.baseline = pending.baseline;
      this.pendingRevision = unconfirmed ? (pending.pendingRevision ?? null) : null;
      const changed =
        initial.revision !== pending.baseline.revision || initial.text !== pending.baseline.text;
      this.incoming = changed ? initial : null;
      this.snapshot = {
        text: pending.text,
        revision: pending.baseline.revision,
        status: changed ? 'conflict' : 'dirty',
        error: changed
          ? 'Recovered unsaved explanation. The saved version changed; choose which version to keep.'
          : null,
      };
    }
  }
  getSnapshot = () => this.snapshot;
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  private set(next: DraftState) {
    this.snapshot = next;
    this.retain(this.pendingExplanation());
    this.listeners.forEach((listener) => listener());
  }
  pendingExplanation(): PendingExplanation | null {
    return this.snapshot.status === 'saved'
      ? null
      : {
          baseline: this.baseline,
          text: this.snapshot.text,
          ...(this.pendingRevision !== null ? { pendingRevision: this.pendingRevision } : {}),
        };
  }

  edit(text: string) {
    this.set({
      ...this.snapshot,
      text,
      error: this.snapshot.status === 'conflict' ? this.snapshot.error : null,
      status:
        this.snapshot.status === 'conflict'
          ? 'conflict'
          : this.saving
            ? 'saving'
            : text === this.baseline.text && this.pendingRevision === null
              ? 'saved'
              : 'dirty',
    });
  }

  refresh(server: DraftVersion) {
    if (
      server.revision <=
      Math.max(this.baseline.revision, this.incoming?.revision ?? -1, this.deferred?.revision ?? -1)
    )
      return;
    if (this.saving) {
      this.deferred = server;
      return;
    }
    const localIntent = this.snapshot.text !== this.baseline.text || this.pendingRevision !== null;
    if (this.pendingRevision !== null && server.revision > this.pendingRevision)
      this.pendingRevision = null;
    if (localIntent && this.snapshot.text !== server.text) {
      this.incoming = server;
      this.set({
        ...this.snapshot,
        status: 'conflict',
        error: 'Your explanation changed in another tab. Choose which version to keep.',
      });
    } else {
      this.baseline = server;
      this.incoming = null;
      this.set({ ...server, status: 'saved', error: null });
    }
  }

  resolveConflict(keepLocal: boolean) {
    if (!this.incoming) return;
    this.baseline = this.incoming;
    this.incoming = null;
    this.pendingRevision = null;
    const text = keepLocal ? this.snapshot.text : this.baseline.text;
    this.set({
      text,
      revision: this.baseline.revision,
      status: text === this.baseline.text ? 'saved' : 'dirty',
      error: null,
    });
  }

  async save(): Promise<void> {
    if (this.saving) return this.saving;
    if (this.snapshot.status === 'conflict')
      throw new Error(this.snapshot.error ?? 'Resolve the explanation conflict.');
    if (this.snapshot.text === this.baseline.text && this.pendingRevision === null) return;
    const input = { text: this.snapshot.text, revision: this.baseline.revision };
    this.pendingRevision = input.revision;
    this.set({ ...this.snapshot, status: 'saving', error: null });
    const pending = (async () => {
      try {
        const saved = await this.persist(input);
        this.baseline = saved;
        this.pendingRevision = null;
        this.set({
          ...this.snapshot,
          revision: saved.revision,
          status: this.snapshot.text === saved.text ? 'saved' : 'dirty',
          error: null,
        });
      } catch (error) {
        this.set({
          ...this.snapshot,
          status: 'error',
          error: error instanceof Error ? error.message : 'Your explanation could not be saved.',
        });
        throw error;
      }
    })();
    this.saving = pending;
    try {
      await pending;
    } finally {
      this.saving = null;
      const deferred = this.deferred;
      this.deferred = null;
      if (deferred) this.refresh(deferred);
    }
  }
  async flush() {
    do {
      await this.save();
    } while (this.snapshot.text !== this.baseline.text || this.pendingRevision !== null);
  }
}
